import { ChangeDetectorRef, Component, ElementRef, NgZone, OnDestroy, OnInit, ViewChild, inject } from '@angular/core';

import { FormsModule } from '@angular/forms';
import { Router, RouterLink, ActivatedRoute } from '@angular/router';
import {
  IonIcon,
  IonSpinner
} from '@ionic/angular/standalone';
import { addIcons } from 'ionicons';
import {
  alertCircleOutline,
  eyeOutline,
  eyeOffOutline,
  mailOutline,
  lockClosedOutline,
  logInOutline
} from 'ionicons/icons';
import { TranslatePipe } from '../../../pipes/translate.pipe';
import { AuthService } from '../../../services/auth.service';
import { GoogleCredentialResponse, GoogleIdentityService } from '../../../services/google-identity.service';

@Component({
  selector: 'app-login',
  standalone: true,
  imports: [
    FormsModule,
    RouterLink,
    IonIcon,
    IonSpinner,
    TranslatePipe
  ],
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss']
})
export class LoginComponent implements OnInit, OnDestroy {
  private readonly ngZone = inject(NgZone);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);
  private readonly googleIdentityService = inject(GoogleIdentityService);
  @ViewChild('emailInput') private emailInput?: ElementRef<HTMLInputElement>;
  @ViewChild('passwordInput') private passwordInput?: ElementRef<HTMLInputElement>;

  email = '';
  password = '';
  rememberMe = false;
  showPassword = false;
  isLoading = false;
  isProviderLoading = true;
  googleAvailable = false;
  errorMessage = '';
  returnUrl = '/';
  formSubmitted = false;

  constructor(
    private authService: AuthService,
    private router: Router,
    private route: ActivatedRoute
  ) {
    addIcons({
      alertCircleOutline,
      eyeOutline,
      eyeOffOutline,
      mailOutline,
      lockClosedOutline,
      logInOutline
    });
  }

  ngOnInit(): void {
    this.returnUrl = this.route.snapshot.queryParams['returnUrl'] || '/';

    // Load remembered email
    const rememberedEmail = localStorage.getItem('remembered_email');
    if (rememberedEmail) {
      this.email = rememberedEmail;
      this.rememberMe = true;
    }

    this.authService.getProviders().subscribe({
      next: (providers) => this.runInAngular(() => {
        this.googleAvailable = providers.google;
        this.isProviderLoading = false;
        if (providers.google && providers.googleClientId && !this.isOneTapSuppressed()) {
          this.initializeGoogleOneTap(providers.googleClientId);
        }
      }),
      error: () => this.runInAngular(() => {
        this.googleAvailable = false;
        this.isProviderLoading = false;
      })
    });

    const oauthError = this.route.snapshot.queryParamMap.get('error');
    if (oauthError) {
      this.errorMessage = this.getOAuthErrorMessage(oauthError);
    }
  }

  togglePasswordVisibility(): void {
    this.showPassword = !this.showPassword;
  }

  onSubmit(): void {
    // Password managers can update native input values without telling NgModel.
    // The submitted credentials must come from the inputs the user can see.
    this.syncCredentialsFromInputs();
    this.formSubmitted = true;

    if (!this.isFormValid()) {
      this.errorMessage = 'LOGIN_FORM_INCOMPLETE';
      return;
    }

    this.isLoading = true;
    this.errorMessage = '';

    this.authService.login({ email: this.email, password: this.password }).subscribe({
      next: (response) => {
        this.runInAngular(() => {
          this.isLoading = false;
          if (response.requiresMfa && response.mfaChallenge) {
            this.router.navigate(['/login/verify'], {
              queryParams: { challenge: response.mfaChallenge.id, returnUrl: this.returnUrl }
            });
            return;
          }
          if (response.success) {
            if (this.rememberMe) {
              localStorage.setItem('remembered_email', this.email);
            } else {
              localStorage.removeItem('remembered_email');
            }
            this.router.navigateByUrl(this.returnUrl);
          }
        });
      },
      error: (error) => {
        this.runInAngular(() => {
          this.isLoading = false;
          this.errorMessage = this.resolveErrorKey(error, 'LOGIN_ERROR');
        });
      }
    });
  }

  ngOnDestroy(): void {
    this.googleIdentityService.cancelOneTap();
  }

  isFormValid(): boolean {
    return this.authService.isEmailValid(this.email) && this.password.length > 0;
  }

  isEmailValid(): boolean {
    return this.email.length === 0 || this.authService.isEmailValid(this.email);
  }

  isEmailInputInvalid(): boolean {
    return this.email.length > 0
      ? !this.authService.isEmailValid(this.email)
      : this.formSubmitted;
  }

  onCredentialsChanged(): void {
    if (this.errorMessage === 'LOGIN_FORM_INCOMPLETE') {
      this.errorMessage = '';
    }
  }

  continueWithGoogle(): void {
    if (this.isLoading || this.isProviderLoading) {
      return;
    }
    if (!this.googleAvailable) {
      this.errorMessage = 'GOOGLE_SIGN_IN_SETUP_PENDING';
      return;
    }
    this.authService.startGoogleSignIn(this.returnUrl);
  }

  private initializeGoogleOneTap(clientId: string): void {
    this.googleIdentityService.initializeOneTap(clientId, (response) => {
      this.runInAngular(() => this.completeGoogleOneTap(response));
    }).catch(() => {
      // The explicit Google button remains available if One Tap is unavailable.
    });
  }

  private completeGoogleOneTap(response: GoogleCredentialResponse): void {
    if (this.isLoading || !response.credential) {
      return;
    }
    this.isLoading = true;
    this.errorMessage = '';

    this.authService.signInWithGoogleCredential(response.credential, this.returnUrl).subscribe({
      next: (result) => this.runInAngular(() => {
        this.isLoading = false;
        if (result.requiresMfa && result.mfaChallenge) {
          void this.router.navigate(['/login/verify'], {
            queryParams: { challenge: result.mfaChallenge.id, returnUrl: this.returnUrl }
          });
          return;
        }
        if (result.success) {
          void this.router.navigateByUrl(result.returnUrl || this.returnUrl);
        }
      }),
      error: (error) => this.runInAngular(() => {
        this.isLoading = false;
        this.errorMessage = this.resolveErrorKey(error, 'GOOGLE_SIGN_IN_FAILED');
      })
    });
  }

  private isOneTapSuppressed(): boolean {
    return sessionStorage.getItem('google_one_tap_suppressed') === 'true';
  }

  private resolveErrorKey(error: any, fallbackKey: string): string {
    const message = error?.error?.message || error?.message;
    if (typeof message === 'string' && /^[A-Z0-9_]+$/.test(message)) {
      return message;
    }
    return fallbackKey;
  }

  private getOAuthErrorMessage(code: string): string {
    switch (code) {
      case 'GOOGLE_ACCOUNT_LINK_REQUIRED':
        return 'GOOGLE_ACCOUNT_LINK_REQUIRED';
      case 'GOOGLE_SIGN_IN_CANCELLED':
        return 'GOOGLE_SIGN_IN_CANCELLED';
      default:
        return 'GOOGLE_SIGN_IN_FAILED';
    }
  }

  private syncCredentialsFromInputs(): void {
    const visibleEmail = this.emailInput?.nativeElement.value;
    const visiblePassword = this.passwordInput?.nativeElement.value;
    if (typeof visibleEmail === 'string') {
      this.email = visibleEmail.trim();
    }
    if (typeof visiblePassword === 'string') {
      this.password = visiblePassword;
    }
  }

  private runInAngular(callback: () => void): void {
    this.ngZone.run(() => {
      callback();
      this.changeDetectorRef.detectChanges();
    });
  }
}
